import { useCallback, useState } from "react";
import { useOutletContext, useParams } from "react-router";
import { Button } from "@/components/Button";
import { TextInput } from "@/components/TextInput";
import { Toast } from "@/components/Toast";
import { plural } from "@/lib/plural";
import { useEntitySettings } from "@/lib/useEntitySettings";
import { ScimNotice } from "./ScimNotice";
import type { TeamLayoutContext } from "./TeamLayout";
import { LoadError } from "@/components/LoadError";

interface Team {
  id: string;
  name: string;
  description: string | null;
  members: { userId: string }[];
  provisionedVia: number;
  isScimManaged: boolean;
}

export function TeamSettings() {
  const { orgId, teamId } = useParams();
  const { onTeamRenamed } = useOutletContext<TeamLayoutContext>();
  const [description, setDescription] = useState("");
  const [memberCount, setMemberCount] = useState(0);
  const initializeFromTeam = useCallback((team: Team) => {
    setDescription(team.description ?? "");
    setMemberCount(team.members.length);
  }, []);

  const settings = useEntitySettings<Team>({
    entityId: teamId,
    endpointSegment: "teams",
    entityLabel: "team",
    buildDeleteConfirmMessage: () =>
      `${plural(memberCount, "member")} lose the access this team grants. This can't be undone.`,
    buildDeleteConfirmTitle: (name) => `Delete ${name}?`,
    deleteSuccessMessage: (name) => `${name} deleted.`,
    afterDeleteNavigateTo: `/${orgId}/teams`,
    onRenamed: onTeamRenamed,
    onLoaded: initializeFromTeam,
    buildPatchBody: (name) => ({ name, description: description || null }),
    saveSuccessMessage: "Changes saved.",
    saveFailureMessage: "Couldn't save team.",
  });

  const team = settings.entity;

  if (settings.error && !team) {
    return <LoadError what="this team" />;
  }

  if (!team) {
    return (
      <div className="animate-pulse space-y-4 rounded-lg bg-surface/40 p-4">
        <div className="h-3 w-24 rounded bg-surface" />
        <div className="h-10 w-full rounded bg-surface" />
        <div className="h-3 w-24 rounded bg-surface" />
        <div className="h-16 w-full rounded bg-surface" />
      </div>
    );
  }

  const isScimManaged = team.isScimManaged;

  return (
    <div className="space-y-6">
      {isScimManaged && (
        <ScimNotice>
          This team is managed by your identity provider. Its name is set there.
        </ScimNotice>
      )}

      <form onSubmit={settings.handleSave} className="space-y-4">
        <div>
          <label
            htmlFor="teamName"
            className="mb-1.5 block text-sm text-foreground"
          >
            Team name
          </label>
          <TextInput
            type="text"
            id="teamName"
            value={settings.name}
            onChange={(e) => settings.setName(e.target.value)}
            required
            disabled={isScimManaged}
            className="w-full disabled:opacity-50"
          />
        </div>
        <div>
          <label
            htmlFor="teamDescription"
            className="mb-1.5 block text-sm text-foreground"
          >
            Description
          </label>
          <textarea
            id="teamDescription"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-muted bg-surface px-3 py-2 text-sm text-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-50"
          />
        </div>
        <div className="flex justify-end">
          <Button
            variant="primary"
            type="submit"
            disabled={
              settings.submitting ||
              (settings.name === team.name &&
                description === (team.description ?? ""))
            }
          >
            {settings.submitting ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>

      <div className="flex items-center justify-between gap-6 rounded-lg border border-danger/30 p-4">
        <div>
          <h2 className="text-sm font-semibold text-foreground">
            Delete this team
          </h2>
          <p className="mt-0.5 max-w-md text-xs text-foreground/70">
            Deleting this team will remove the access it grants to{" "}
            {plural(memberCount, "member")}.
          </p>
        </div>
        <Button
          variant="dangerOutline"
          onClick={settings.handleDelete}
          disabled={isScimManaged || settings.submitting}
          className="shrink-0"
        >
          Delete team
        </Button>
      </div>

      <Toast toast={settings.toast} />

      {settings.confirmDialog}
    </div>
  );
}
